"use client"

import { useCallback, useRef, useState } from "react"
import { HttpTypes } from "@medusajs/types"
import { Table, Heading, Text, clx } from "@medusajs/ui"
import { updateLineItem, deleteLineItem } from "@lib/data/cart"
import { emitCartUpdate } from "@lib/cart-event"
import CartItemSelect from "@modules/cart/components/cart-item-select"
import ErrorMessage from "@modules/checkout/components/error-message"
import LineItemOptions from "@modules/common/components/line-item-options"
import LineItemPrice from "@modules/common/components/line-item-price"
import LineItemUnitPrice from "@modules/common/components/line-item-unit-price"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Spinner from "@modules/common/icons/spinner"
import Thumbnail from "@modules/products/components/thumbnail"
import { Trash } from "lucide-react"
import { useRouter } from "next/navigation"

type Props = {
  items?: HttpTypes.StoreCartLineItem[]
  checkedIds: Set<string>
  onCheckedIdsChange: (ids: Set<string>) => void
}

function CartItemRow({
  item,
  checked,
  onToggle,
}: {
  item: HttpTypes.StoreCartLineItem
  checked: boolean
  onToggle: () => void
}) {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [optimisticQty, setOptimisticQty] = useState<number | null>(null)
  const router = useRouter()

  const { handle } = item.variant?.product ?? {}
  const displayQuantity = optimisticQty ?? item.quantity
  const maxQuantity = item.variant?.manage_inventory ? 10 : 10

  const changeQuantity = async (quantity: number) => {
    setError(null)
    setUpdating(true)
    setOptimisticQty(quantity)
    await updateLineItem({ lineId: item.id, quantity })
      .then(() => { emitCartUpdate(); router.refresh() })
      .catch((err) => { setOptimisticQty(null); setError(err.message) })
      .finally(() => setUpdating(false))
  }

  const handleDelete = async () => {
    await deleteLineItem(item.id)
    emitCartUpdate()
    router.refresh()
  }

  return (
    <Table.Row
      className={clx("w-full transition-colors", { "bg-blue-50": checked })}
      data-testid="product-row"
    >
      {/* Checkbox */}
      <Table.Cell className="!pl-0 w-10 pr-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${item.product_title}`}
          className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-[var(--primary)]"
        />
      </Table.Cell>

      {/* Thumbnail */}
      <Table.Cell className="!pl-0 p-4 w-24">
        <LocalizedClientLink
          href={`/products/${handle}`}
          className="flex small:w-24 w-12"
        >
          <Thumbnail
            thumbnail={item.variant?.product?.thumbnail}
            images={item.variant?.product?.images}
            size="square"
          />
        </LocalizedClientLink>
      </Table.Cell>

      {/* Title */}
      <Table.Cell className="text-left">
        <Text className="txt-medium-plus text-ui-fg-base" data-testid="product-title">
          {item.product_title}
        </Text>
        <LineItemOptions variant={item.variant} data-testid="product-variant" />
      </Table.Cell>

      {/* Qty + delete */}
      <Table.Cell>
        <div className="flex gap-2 items-center w-28">
          <button
            onClick={handleDelete}
            className="text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Remove item"
            data-testid="product-delete-button"
          >
            <Trash size={16} />
          </button>
          <CartItemSelect
            value={displayQuantity}
            onChange={(e) => changeQuantity(parseInt(e.target.value))}
            className="w-14 h-10 p-4"
            data-testid="product-select-button"
          >
            {Array.from({ length: Math.min(maxQuantity, 10) }, (_, i) => (
              <option value={i + 1} key={i}>{i + 1}</option>
            ))}
          </CartItemSelect>
          {updating && <Spinner />}
        </div>
        <ErrorMessage error={error} data-testid="product-error-message" />
      </Table.Cell>

      {/* Unit price */}
      <Table.Cell className="hidden small:table-cell">
        <LineItemUnitPrice item={item} style="tight" />
      </Table.Cell>

      {/* Total */}
      <Table.Cell className="!pr-0">
        <LineItemPrice item={item} style="tight" />
      </Table.Cell>
    </Table.Row>
  )
}

function SelectAllCheckbox({
  allChecked,
  someChecked,
  onToggle,
}: {
  allChecked: boolean
  someChecked: boolean
  onToggle: () => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  if (ref.current) {
    ref.current.indeterminate = someChecked && !allChecked
  }
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={allChecked}
      onChange={onToggle}
      aria-label="Select all items"
      className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-[var(--primary)]"
    />
  )
}

const ItemsTemplate = ({ items, checkedIds, onCheckedIdsChange }: Props) => {
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const router = useRouter()

  const sortedItems = items
    ? [...items].sort((a, b) =>
        (a.created_at ?? "") > (b.created_at ?? "") ? -1 : 1
      )
    : []

  const allChecked = sortedItems.length > 0 && sortedItems.every((i) => checkedIds.has(i.id))
  const someChecked = sortedItems.some((i) => checkedIds.has(i.id))

  const toggleAll = useCallback(() => {
    if (allChecked) {
      onCheckedIdsChange(new Set())
    } else {
      onCheckedIdsChange(new Set(sortedItems.map((i) => i.id)))
    }
  }, [allChecked, sortedItems, onCheckedIdsChange])

  const toggleItem = useCallback((id: string) => {
    const next = new Set(checkedIds)
    next.has(id) ? next.delete(id) : next.add(id)
    onCheckedIdsChange(next)
  }, [checkedIds, onCheckedIdsChange])

  const deleteSelected = useCallback(async () => {
    if (checkedIds.size === 0) return
    setBulkDeleting(true)
    try {
      for (const id of Array.from(checkedIds)) {
        await deleteLineItem(id)
      }
      emitCartUpdate()
      onCheckedIdsChange(new Set())
      router.refresh()
    } finally {
      setBulkDeleting(false)
    }
  }, [checkedIds, router, onCheckedIdsChange])

  return (
    <div>
      <div className="pb-3 flex items-center justify-between gap-4">
        <Heading className="text-[2rem] leading-[2.75rem]">Cart</Heading>
        {someChecked && (
          <button
            onClick={deleteSelected}
            disabled={bulkDeleting}
            className="text-sm text-red-500 hover:text-red-700 font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <Trash size={14} />
            {bulkDeleting
              ? "Removing…"
              : `Remove selected (${checkedIds.size})`}
          </button>
        )}
      </div>
      <Table>
        <Table.Header className="border-t-0">
          <Table.Row className="text-ui-fg-subtle txt-medium-plus">
            <Table.HeaderCell className="!pl-0 w-10 pr-2">
              <SelectAllCheckbox
                allChecked={allChecked}
                someChecked={someChecked}
                onToggle={toggleAll}
              />
            </Table.HeaderCell>
            <Table.HeaderCell className="!pl-0">Item</Table.HeaderCell>
            <Table.HeaderCell></Table.HeaderCell>
            <Table.HeaderCell>Quantity</Table.HeaderCell>
            <Table.HeaderCell className="hidden small:table-cell">Price</Table.HeaderCell>
            <Table.HeaderCell className="!pr-0 text-right">Total</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedItems.map((item) => (
            <CartItemRow
              key={item.id}
              item={item}
              checked={checkedIds.has(item.id)}
              onToggle={() => toggleItem(item.id)}
            />
          ))}
        </Table.Body>
      </Table>
    </div>
  )
}

export default ItemsTemplate
